// Phase 85.A.72 — Avatar proposal gate (admin-initiated avatar refresh).
//
// Mounts AvatarProposalRequiredModal whenever the logged-in athlete has any
// pending avatar_proposals row that is NOT deferred (defer_until <= now).
//
// v17.perf.har2 (02/2026) — Migrate sang React Query để share cache với
// bất kỳ consumer nào cùng queryKey. StrictMode/route change không refetch.
// Polling 60s giữ nguyên qua `refetchInterval`.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import AvatarProposalRequiredModal from "../components/athlete/AvatarProposalRequiredModal";
import { api } from "../lib/api";
import { isUploadActive } from "../lib/uploadActivity";

const ESCAPE_PATHS = ["/login", "/register", "/auth/callback", "/logout", "/403"];
const POLL_INTERVAL_MS = 60_000;

export const AVATAR_PROPOSALS_QUERY_KEY = ["athlete", "avatar-proposals"];

export function AvatarProposalGateProvider({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();

  const onEscapeRoute = ESCAPE_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );

  // Phase 85.A.72 Q8 — Stack lần lượt. Đợi phone gate clear trước (Google
  // user chưa verify SĐT thì PhoneVerifyRequiredModal sẽ phủ kín ở trên).
  const phoneGateActive =
    !loading && !!user && user.auth_provider === "google" && user.is_phone_verified === false;

  const shouldFetch = !loading && !!user && !onEscapeRoute && !phoneGateActive;

  const { data: items, refetch } = useQuery({
    queryKey: AVATAR_PROPOSALS_QUERY_KEY,
    enabled: shouldFetch,
    queryFn: async () => {
      if (isUploadActive()) return [];
      try {
        const r = await api.get("/athlete/avatar-proposals");
        return Array.isArray(r.data) ? r.data : [];
      } catch {
        return [];
      }
    },
    staleTime: Infinity,
    refetchInterval: shouldFetch ? POLL_INTERVAL_MS : false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // v17.perf.har3 (13/08/2026) — Xoá custom `focus` handler gọi `refetch()`
  // (regression trong v17.perf.har2 khiến avatar-proposals tăng 3x→7x). Custom
  // event `avatar-proposal-changed` giữ nguyên vì được dispatch từ nơi khác
  // sau khi user resolve (không phải browser focus).
  useEffect(() => {
    if (!shouldFetch) return undefined;
    const onCustom = () => refetch();
    window.addEventListener("avatar-proposal-changed", onCustom);
    return () => {
      window.removeEventListener("avatar-proposal-changed", onCustom);
    };
  }, [shouldFetch, refetch]);

  const activeProposal = Array.isArray(items) && items.length ? items[0] : null;

  const handleResolved = () => {
    // Optimistic: xoá khỏi cache + refetch để catch follow-up.
    qc.setQueryData(AVATAR_PROPOSALS_QUERY_KEY, (prev) =>
      Array.isArray(prev) && activeProposal
        ? prev.filter((p) => p.id !== activeProposal.id)
        : [],
    );
    setTimeout(() => refetch(), 1500);
  };

  return (
    <>
      {children}
      {shouldFetch && activeProposal ? (
        <AvatarProposalRequiredModal
          proposal={activeProposal}
          onResolved={handleResolved}
        />
      ) : null}
    </>
  );
}
